import { apiClient } from '../../shared/api/client'
import type {
  FeedbackCommentDto,
  FeedbackDto,
  FeedbackListResponse,
  FeedbackType,
  FeedbackPriority,
  FeedbackStatus,
} from '@crm/shared'

export interface UpdateFeedbackContentInput {
  type?: FeedbackType
  priority?: FeedbackPriority
  title?: string
  description?: string | null
}

export interface FeedbackListParams {
  type?: FeedbackType
  priority?: FeedbackPriority
  status?: FeedbackStatus
  authorId?: string
  page?: number
  limit?: number
}

export async function listFeedback(params: FeedbackListParams = {}): Promise<FeedbackListResponse> {
  const { data } = await apiClient.get('/feedback', { params })
  return data.data
}

export async function getFeedback(id: string): Promise<FeedbackDto> {
  const { data } = await apiClient.get(`/feedback/${id}`)
  return data.data
}

export async function createFeedback(input: {
  type: FeedbackType
  priority: FeedbackPriority
  title: string
  description?: string | null
  screenshot?: File | null
}): Promise<FeedbackDto> {
  if (input.screenshot) {
    const fd = new FormData()
    fd.append('type', input.type)
    fd.append('priority', input.priority)
    fd.append('title', input.title)
    if (input.description) fd.append('description', input.description)
    fd.append('screenshot', input.screenshot)
    const { data } = await apiClient.post('/feedback', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data.data
  }
  const { data } = await apiClient.post('/feedback', {
    type: input.type,
    priority: input.priority,
    title: input.title,
    description: input.description ?? null,
  })
  return data.data
}

export async function updateFeedbackStatus(
  id: string,
  status: FeedbackStatus,
): Promise<FeedbackDto> {
  const { data } = await apiClient.patch(`/feedback/${id}/status`, { status })
  return data.data
}

export async function updateFeedbackReply(id: string, adminReply: string): Promise<FeedbackDto> {
  const { data } = await apiClient.patch(`/feedback/${id}/reply`, { adminReply })
  return data.data
}

export async function updateFeedbackContent(
  id: string,
  data: UpdateFeedbackContentInput,
): Promise<FeedbackDto> {
  const { data: res } = await apiClient.patch(`/feedback/${id}`, data)
  return res.data
}

export async function deleteFeedback(id: string): Promise<void> {
  await apiClient.delete(`/feedback/${id}`)
}

export async function addFeedbackComment(
  feedbackId: string,
  input: { text: string; screenshot?: File | null },
): Promise<FeedbackCommentDto> {
  if (input.screenshot) {
    const fd = new FormData()
    fd.append('text', input.text)
    fd.append('screenshot', input.screenshot)
    const { data } = await apiClient.post(`/feedback/${feedbackId}/comments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data.data
  }
  const { data } = await apiClient.post(`/feedback/${feedbackId}/comments`, { text: input.text })
  return data.data
}

export async function deleteFeedbackComment(feedbackId: string, commentId: string): Promise<void> {
  await apiClient.delete(`/feedback/${feedbackId}/comments/${commentId}`)
}
